/**
 * Implementation extended from:
 * https://www.contentful.com/developers/docs/tutorials/general/getting-started-with-rich-text-field-type/
 * https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
 */

/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import type { Document, Text } from '@contentful/rich-text-types';
import {
  ArticleVideoEmbedder,
  SharedArticlesRichText,
  SocialMediaPost,
  getDefaultArticlesRichTextRenderNodes,
} from '@coursera-web/shared-articles';
import { kebabCase } from 'lodash';

import Markdown from 'bundles/cms/components/Markdown';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import PromoUnit from 'bundles/unified-seo-content-common/components/promo-unit/PromoUnit';
import ConfigurableImage from 'bundles/unified-seo-content-common/components/rich-text/ConfigurableImage';
import Faqs from 'bundles/unified-seo-content-common/components/rich-text/Faqs';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import Highlight from 'bundles/unified-seo-content-common/components/rich-text/Highlight';
import RecommendationCollection from 'bundles/unified-seo-content-common/components/rich-text/RecommendationCollection';
import EmbedTypeDelegator from 'bundles/unified-seo-content-common/components/rich-text/delegators/EmbedTypeDelegator';
import { PROMO_UNIT_TYPES, richTextEmbedTypes } from 'bundles/unified-seo-content-common/constants';
import type { SeoContentLinksFragment } from 'bundles/unified-seo-content-common/types';
import { getAllFaqEntries } from 'bundles/unified-seo-content-common/utils';

type OwnProps = {
  content: Document;
  links?: SeoContentLinksFragment;
  getRichTextOptions?: (links?: SeoContentLinksFragment) => Options;
};

type Props = OwnProps & Omit<React.HTMLProps<HTMLDivElement>, keyof OwnProps>;

export const getDefaultRichTextOptions = (links?: SeoContentLinksFragment): Options => {
  let faqIdx = -1;
  let headingIdx = -1;
  return {
    /**
     * This method is ran, in order, for every node in the rich text body. It can be used to 
     * specify how documentToReactComponents should render specific nodes. 

     * When it is run, we know the location of the current node, but nothing about the node 
     * itself. To counter this we use the id attribute to find the asset, entry, etc corresponding 
     * to the current node, then render accordingly.
     */
    renderNode: {
      ...getDefaultArticlesRichTextRenderNodes(),
      /**
       * Renders embedded assets.
       */
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const id = node?.data?.target?.sys?.id;

        const assetMetadata = links?.assets?.block?.find((link) => id === link?.sys?.id);

        const imageSource = assetMetadata?.url;
        const imageTitle = assetMetadata?.title;
        const imageAltText = assetMetadata?.description;

        if (!imageSource || !imageAltText) {
          return null;
        }

        return (
          <figure>
            <img src={imageSource} alt={imageAltText} />
            {imageTitle && <figcaption>{imageTitle}</figcaption>}
          </figure>
        );
      },
      [BLOCKS.HEADING_2]: (node, children) => {
        const textBlock = node?.content?.[0] as Text;
        const kebabCaseTextContent = kebabCase(textBlock?.value);
        headingIdx += 1;
        return <h2 id={`${headingIdx}-${kebabCaseTextContent}`}>{children}</h2>;
      },
      /**
       * Renders embedded entries.
       */
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const id = node?.data?.target?.sys?.id;
        const entry = links?.entries?.block?.find((link) => link?.sys?.id === id);

        switch (entry?.__typename) {
          case richTextEmbedTypes.articleDegreeRecommendations:
            return (
              <RecommendationCollection
                degreeCategory={entry?.category || null}
                productType={entry?.productType || null}
              />
            );
          case richTextEmbedTypes.articleVlpOrEntityEmbed: // Article embed component that supports embedding vlps and entity cards
            // We don't want any chance of the whole page crashing if something goes wrong with the component.  This error boundary logs and renders nothing in case of error.
            return (
              <ErrorBoundaryWithTagging componentName={entry.embedType || richTextEmbedTypes.articleVlpOrEntityEmbed}>
                <EmbedTypeDelegator embedMetadata={entry} />
              </ErrorBoundaryWithTagging>
            );
          case richTextEmbedTypes.articlePromoUnit: // Article Coursera Plus promo unit component
            if (
              !entry?.type ||
              (entry.type !== PROMO_UNIT_TYPES.courseraPlus && entry.type !== PROMO_UNIT_TYPES.courseraPlusPartnerLogos)
            ) {
              return null;
            }
            return <PromoUnit type={entry.type} />;
          case richTextEmbedTypes.articleHighlight: // Article highlight component
            if (!entry?.body) {
              return null;
            }
            return <Highlight body={entry.body} />;
          case richTextEmbedTypes.articleTable: // Article table component
            if (!entry.description) {
              return null;
            }
            return <Markdown className="rc-ArticleTable" source={entry.description} escapeHtml={false} />;
          case richTextEmbedTypes.articleImageLink:
            if (!entry.image || !entry.image.description || !entry.image.url) {
              return null;
            }
            return (
              <ConfigurableImage
                configurableImage={{
                  ...entry,
                  url: entry.image.url,
                  description: entry.image.description,
                  title: entry.image.title,
                }}
              />
            );
          case richTextEmbedTypes.faq: {
            faqIdx += 1;

            // For all subsequent faqs past the first one, we will return null
            if (faqIdx > 0) return null;

            // For the first faq, we try to render the entire list
            const allFaqEntries = getAllFaqEntries(links);

            if (allFaqEntries === undefined) return null;

            return <Faqs faqs={allFaqEntries} />;
          }
          case richTextEmbedTypes.articleEmbeddedEntry:
            if (entry?.socialMediaEmbed) {
              return (
                <ErrorBoundaryWithTagging componentName="socialMediaEmbed">
                  <SocialMediaPost htmlString={entry.socialMediaEmbed} />
                </ErrorBoundaryWithTagging>
              );
            }

            if ((!entry.video?.url && !entry.externalEmbedVideoUrl) || !entry.previewImage) {
              return null;
            }

            return (
              <ArticleVideoEmbedder
                video={entry.video}
                externalEmbedVideoUrl={entry.externalEmbedVideoUrl}
                title={entry.title}
                previewImage={entry.previewImage}
              />
            );
          default:
            return null;
        }
      },
      /**
       * Renders embedded links.
       */
    },
    renderMark: {
      superscript: (text) => {
        return <sup>{text}</sup>;
      },
    },
  };
};

const RichText = ({ content, links, getRichTextOptions = getDefaultRichTextOptions, ...divElementProps }: Props) => {
  return <SharedArticlesRichText content={content} options={getRichTextOptions(links)} {...divElementProps} />;
};

export default RichText;
